﻿/**
 * GroupHeroBanner — Immersive banner header for the group detail page.
 * Adapts to company branding (gradient, logo, top color strip) when
 * `group.companyBranding` is present; falls back to Zporti purple theme.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, Pressable, Platform, Share, StyleSheet, useWindowDimensions } from 'react-native';
import { Image } from 'expo-image';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { ChevronLeft, Copy, MoreVertical, UserPlus } from 'lucide-react-native';
import { buildCompetitionSquadJoinUrl } from '../../../src/competitions/helpers/competitionInviteLink.helper';
import { useCompetitionId } from '../../../src/competitions/contexts/CompetitionContext';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { copyToClipboardFallback } from '../../../src/shared/helpers/clipboard.helper';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { getGroupHeroBannerStyles } from './GroupHeroBanner.styles';
import {
  GROUP_HERO_BANNER_BACK_HIT_SLOP,
  GROUP_HERO_BANNER_COPY,
  GROUP_HERO_BANNER_ICON_COLOR,
  GROUP_HERO_BANNER_ICON_COLOR_MUTED,
  GROUP_HERO_BANNER_ICON_COLOR_STRONG,
  GROUP_HERO_BANNER_ORB_GRADIENT,
  GROUP_HERO_BANNER_SHIMMER_GRADIENT,
  GROUP_HERO_BANNER_SMALL_SCREEN_WIDTH,
  GROUP_HERO_BANNER_COMPANY_CREST_SIZE,
  GROUP_HERO_BANNER_TV_CREST_SIZE,
  GROUP_HERO_BANNER_IMAGE_CONTENT_POSITION,
  GROUP_HERO_BANNER_LEFT_SCRIM_GRADIENT,
  buildShareMessage,
  getGroupHeroBannerHeroMaxWidth,
  getGroupHeroBannerNameColumnMaxWidth,
  getGroupHeroBannerLeftScrimWidth,
  getGroupHeroBannerTvHeight,
  getShareDialogTitle,
} from './GroupHeroBanner.constants';
import { WC_GROUP_CREATED_COPY } from '../WorldCupGroupCreatedScreen/WorldCupGroupCreatedScreen.constants';
import { WorldCupCrestPreview } from '../WorldCupCrestPreview';
import { deriveGroupCrest } from '../../../src/worldcup/helpers/worldCupCrest.helper';
import type { CrestColorId, CrestEmblemId } from '../CreateWorldCupGroupScreen/CreateWorldCupGroupScreen.constants';
import type { WorldCupGroup } from '../../../src/worldcup/types/worldcup.types';

const DEFAULT_MONKEY_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.idle);

interface GroupHeroBannerLabelOverrides {
  back?: string
  companyGroup?: string
  employees?: string
  members?: string
}

interface GroupHeroBannerProps {
  group: WorldCupGroup;
  onBack: () => void;
  onInfoPress?: () => void;
  /** Optional copy overrides (e.g. org-admin branding preview). */
  labelOverrides?: GroupHeroBannerLabelOverrides;
  /** Optional trailing control (e.g. squad TV mode toggle). */
  trailingAction?: React.ReactNode;
  /** TV display: hide back/share/info; keep banner, crest, and squad name. */
  tvDisplayMode?: boolean;
  testID?: string;
}

export const GroupHeroBanner: React.FC<GroupHeroBannerProps> = ({
  group,
  onBack,
  onInfoPress,
  labelOverrides,
  trailingAction,
  tvDisplayMode = false,
  testID,
}) => {
  const theme = useTheme();
  const competitionId = useCompetitionId();
  const { showSnackbar } = useSnackbar();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isSmallScreen = windowWidth < GROUP_HERO_BANNER_SMALL_SCREEN_WIDTH;
  const tvBannerHeight = useMemo(
    () => getGroupHeroBannerTvHeight(windowHeight),
    [windowHeight],
  );
  const branding = group.companyBranding ?? null;
  const isCompany = branding !== null;
  const inviteCode =
    group.inviteCode != null && String(group.inviteCode).trim().length > 0
      ? String(group.inviteCode).trim()
      : null;

  const bannerImageUri = branding?.bannerUrl ?? null;
  const hasBannerImage = bannerImageUri != null;

  const styles = useMemo(
    () => {
      let heroMaxWidth;
      if (hasBannerImage && tvDisplayMode) {
        heroMaxWidth = getGroupHeroBannerHeroMaxWidth(windowWidth, true);
      } else if (hasBannerImage) {
        heroMaxWidth = getGroupHeroBannerNameColumnMaxWidth(windowWidth, isSmallScreen);
      }
      return getGroupHeroBannerStyles(theme, branding?.primaryColor, {
        tvDisplayMode,
        tvBannerHeight: tvDisplayMode ? tvBannerHeight : undefined,
        heroMaxWidth,
        leftScrimWidth: hasBannerImage
          ? getGroupHeroBannerLeftScrimWidth(windowWidth)
          : undefined,
      });
    },
    [
      theme,
      branding?.primaryColor,
      tvDisplayMode,
      tvBannerHeight,
      hasBannerImage,
      windowWidth,
      isSmallScreen,
    ],
  );

  const mascotUri = useMemo(
    () => {
      if (branding?.monkeyIdleUrl) return branding.monkeyIdleUrl;
      if (branding?.monkeyIdlePath) return getAssetUrl(branding.monkeyIdlePath);
      return DEFAULT_MONKEY_URI;
    },
    [branding?.monkeyIdleUrl, branding?.monkeyIdlePath],
  );

  const gradientColors = useMemo((): [string, string, string] => {
    if (branding) {
      const s = branding.secondaryColor ?? branding.primaryColor;
      return [branding.primaryColor, branding.primaryColor, s];
    }
    return [theme.primaryDark, theme.primary, theme.primaryLight];
  }, [branding, theme]);

  const orbColors = GROUP_HERO_BANNER_ORB_GRADIENT;

  const { colorId: crestColorId, emblemId: crestEmblemId } = useMemo(() => {
    const derived = deriveGroupCrest(group.id, group.name);
    return {
      colorId: (group.crestColor as CrestColorId | null | undefined) ?? derived.colorId,
      emblemId: (group.crestEmblem as CrestEmblemId | null | undefined) ?? derived.emblemId,
    };
  }, [group.id, group.name, group.crestColor, group.crestEmblem]);

  const memberCountLabel = isCompany
    ? (labelOverrides?.employees ?? GROUP_HERO_BANNER_COPY.EMPLOYEES)
    : (labelOverrides?.members ?? GROUP_HERO_BANNER_COPY.MEMBERS);

  const handleCopyGroupCode = useCallback(async () => {
    if (!inviteCode) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(inviteCode);
        } else {
          copyToClipboardFallback(inviteCode);
        }
      } else {
        await Share.share({ message: inviteCode });
      }
      showSnackbar(WC_GROUP_CREATED_COPY.CODE_COPIED, 'success');
    } catch {
      /* user cancelled share */
    }
  }, [inviteCode, showSnackbar]);

  const handleShareInvite = useCallback(async () => {
    if (!inviteCode) return;
    const inviteLink = buildCompetitionSquadJoinUrl(competitionId, inviteCode, 'b2b');
    if (!inviteLink) return;
    const shareMessage = buildShareMessage(group.name, inviteLink);
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title: getShareDialogTitle(),
          text: shareMessage,
        });
      } else {
        await Share.share({ message: shareMessage });
      }
    } catch {
      /* user cancelled */
    }
  }, [group.name, inviteCode]);

  const companyLogoUri =
    isCompany && branding?.logoUrl != null && String(branding.logoUrl).trim().length > 0
      ? String(branding.logoUrl).trim()
      : null;
  const hasCompanyLogo = companyLogoUri != null;

  const crestSize = tvDisplayMode
    ? GROUP_HERO_BANNER_TV_CREST_SIZE
    : isCompany
      ? GROUP_HERO_BANNER_COMPANY_CREST_SIZE
      : 56;

  const badgeBlock = (
    <View
      style={[
        !tvDisplayMode && styles.badge,
        !tvDisplayMode && styles.badgeForeground,
        tvDisplayMode && styles.tvBadge,
        !tvDisplayMode && isCompany && styles.badgeLogo,
        tvDisplayMode && isCompany && styles.tvBadgeLogo,
        (!isCompany || !hasCompanyLogo) && styles.badgeCrest,
      ]}
    >
      {hasCompanyLogo ? (
        <Image
          source={{ uri: companyLogoUri }}
          style={tvDisplayMode ? styles.tvBadgeLogoImage : styles.badgeLogoImage}
          contentFit="contain"
          alt={branding?.companyName}
        />
      ) : (
        <WorldCupCrestPreview
          colorId={crestColorId}
          emblemId={crestEmblemId}
          name={group.name}
          size={crestSize}
        />
      )}
    </View>
  );

  const heroInfoBlock = (
    <View
      style={
        tvDisplayMode
          ? [
              styles.tvHeroInfo,
              hasBannerImage && styles.tvHeroInfoWithBannerImage,
            ]
          : [
              styles.heroInfo,
              isCompany && styles.heroInfoWithLogoUrl,
              hasBannerImage && styles.heroInfoWithBannerImage,
            ]
      }
    >
      {isCompany ? (
        <View style={[styles.typeBadge, tvDisplayMode && styles.tvTypeBadge]}>
          <Text style={[styles.typeBadgeText, tvDisplayMode && styles.tvTypeBadgeText]}>
            {labelOverrides?.companyGroup ?? GROUP_HERO_BANNER_COPY.COMPANY_GROUP}
          </Text>
        </View>
      ) : null}
      <Text
        style={[
          !tvDisplayMode && styles.groupName,
          isSmallScreen && !tvDisplayMode && styles.groupNameSmall,
          tvDisplayMode && styles.tvGroupName,
          hasBannerImage && !tvDisplayMode && styles.groupNameOnBannerImage,
        ]}
        numberOfLines={tvDisplayMode ? 2 : 2}
      >
        {group.name}
      </Text>
      <Text
        style={[
          !tvDisplayMode && styles.groupMeta,
          tvDisplayMode && styles.tvGroupMeta,
          hasBannerImage && !tvDisplayMode && styles.groupMetaOnBannerImage,
        ]}
        numberOfLines={1}
      >
        {group.memberCount} {memberCountLabel}
      </Text>
    </View>
  );

  return (
    <View style={styles.banner} testID={testID}>
      {/* Background: brand banner image when provided, else gradient */}
      {bannerImageUri ? (
        <Image
          source={{ uri: bannerImageUri }}
          style={styles.gradientFill}
          contentFit="cover"
          contentPosition={GROUP_HERO_BANNER_IMAGE_CONTENT_POSITION}
          cachePolicy="memory-disk"
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
      ) : (
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradientFill}
        />
      )}

      {tvDisplayMode && companyLogoUri && !bannerImageUri ? (
        <View style={styles.tvBlurredLogoBackdrop} pointerEvents="none">
          <Image
            source={{ uri: companyLogoUri }}
            style={styles.tvBlurredLogoImage}
            contentFit="cover"
            cachePolicy="memory-disk"
            alt={WEB_IMAGE_ALT_DECORATIVE}
          />
          {Platform.OS !== 'web' ? (
            <BlurView intensity={55} tint="dark" style={StyleSheet.absoluteFill} />
          ) : null}
        </View>
      ) : null}

      {hasBannerImage ? (
        <LinearGradient
          colors={GROUP_HERO_BANNER_LEFT_SCRIM_GRADIENT}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={styles.bannerLeftScrim}
          pointerEvents="none"
        />
      ) : null}

      {tvDisplayMode && bannerImageUri ? (
        <View style={styles.tvBannerScrim} pointerEvents="none" />
      ) : null}

      {/* Diagonal shimmer overlay */}
      <LinearGradient
        colors={GROUP_HERO_BANNER_SHIMMER_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.textureOverlay}
        pointerEvents="none"
      />

      {/* Radial orb (top-right glow) */}
      <LinearGradient
        colors={orbColors}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={styles.orbContainer}
        pointerEvents="none"
      />

      {tvDisplayMode ? (
        trailingAction != null ? (
          <View style={styles.tvDisplayTrailingRow} pointerEvents="box-none">
            {trailingAction}
          </View>
        ) : null
      ) : isCompany && branding ? (
        <View style={styles.companyStrip}>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.backButtonInStrip,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.backButtonPressed,
              hovered && Platform.OS === 'web' && styles.backButtonHover,
            ]}
            onPress={onBack}
            hitSlop={GROUP_HERO_BANNER_BACK_HIT_SLOP}
          >
            <ChevronLeft size={18} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.5} />
            <Text style={styles.backLabel}>{labelOverrides?.back ?? GROUP_HERO_BANNER_COPY.BACK}</Text>
          </Pressable>
          <View style={styles.companyStripTrailing}>
            {trailingAction}
            {inviteCode ? (
              <>
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.groupCodePillInStrip,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.groupCodePillPressed,
                    hovered && Platform.OS === 'web' && styles.groupCodePillHover,
                  ]}
                  onPress={() => {
                    void handleCopyGroupCode();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={GROUP_HERO_BANNER_COPY.COPY_GROUP_CODE_A11Y}
                  hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                >
                  <Text style={styles.groupCodeLabel}>{GROUP_HERO_BANNER_COPY.GROUP_CODE_LABEL}</Text>
                  <Text style={styles.groupCodeValueStrip} selectable>
                    {inviteCode}
                  </Text>
                  <Copy size={12} color={GROUP_HERO_BANNER_ICON_COLOR_MUTED} strokeWidth={2.5} />
                </Pressable>
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.shareInviteButton,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.shareInviteButtonPressed,
                    hovered && Platform.OS === 'web' && styles.shareInviteButtonHover,
                  ]}
                  onPress={() => {
                    void handleShareInvite();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={GROUP_HERO_BANNER_COPY.SHARE_INVITE_A11Y}
                  hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                >
                  <UserPlus size={14} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.2} />
                </Pressable>
              </>
            ) : null}
            {onInfoPress != null && (
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.infoButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.infoButtonPressed,
                  hovered && Platform.OS === 'web' && styles.infoButtonHover,
                ]}
                onPress={onInfoPress}
                accessibilityRole="button"
                accessibilityLabel={GROUP_HERO_BANNER_COPY.SQUAD_INFO_A11Y}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              >
                <MoreVertical size={14} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.2} />
              </Pressable>
            )}
          </View>
        </View>
      ) : (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.backButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.backButtonPressed,
            hovered && Platform.OS === 'web' && styles.backButtonHover,
          ]}
          onPress={onBack}
          hitSlop={GROUP_HERO_BANNER_BACK_HIT_SLOP}
        >
          <ChevronLeft size={20} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.5} />
          <Text style={styles.backLabel}>{labelOverrides?.back ?? GROUP_HERO_BANNER_COPY.BACK}</Text>
        </Pressable>
      )}

      {!tvDisplayMode && !isCompany && (inviteCode != null || onInfoPress != null || trailingAction != null) ? (
        <View style={styles.groupCodeFloatingCluster} pointerEvents="box-none">
          {trailingAction}
          {inviteCode != null && (
            <>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.groupCodePillFloating,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.groupCodePillPressed,
                  hovered && Platform.OS === 'web' && styles.groupCodePillHover,
                ]}
                onPress={() => {
                  void handleCopyGroupCode();
                }}
                accessibilityRole="button"
                accessibilityLabel={GROUP_HERO_BANNER_COPY.COPY_GROUP_CODE_A11Y}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={styles.groupCodeLabel}>{GROUP_HERO_BANNER_COPY.GROUP_CODE_LABEL}</Text>
                <Text style={styles.groupCodeValue} selectable>
                  {inviteCode}
                </Text>
                <Copy size={14} color={GROUP_HERO_BANNER_ICON_COLOR_STRONG} strokeWidth={2.5} />
              </Pressable>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.shareInviteButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.shareInviteButtonPressed,
                  hovered && Platform.OS === 'web' && styles.shareInviteButtonHover,
                ]}
                onPress={() => {
                  void handleShareInvite();
                }}
                accessibilityRole="button"
                accessibilityLabel={GROUP_HERO_BANNER_COPY.SHARE_INVITE_A11Y}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <UserPlus size={15} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.2} />
              </Pressable>
            </>
          )}
          {onInfoPress != null && (
            <Pressable
              style={({ pressed, hovered }) => [
                styles.infoButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.infoButtonPressed,
                hovered && Platform.OS === 'web' && styles.infoButtonHover,
              ]}
              onPress={onInfoPress}
              accessibilityRole="button"
              accessibilityLabel={GROUP_HERO_BANNER_COPY.SQUAD_INFO_A11Y}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MoreVertical size={15} color={GROUP_HERO_BANNER_ICON_COLOR} strokeWidth={2.2} />
            </Pressable>
          )}
        </View>
      ) : null}

      {tvDisplayMode ? (
        <View
          style={[styles.tvHeroRow, isCompany && styles.tvHeroRowWithMascot]}
          pointerEvents="box-none"
        >
          {badgeBlock}
          {heroInfoBlock}
        </View>
      ) : (
        <>
          {badgeBlock}
          {heroInfoBlock}
        </>
      )}
      {/* Monkey mascot — company squads only; larger on the right in TV display mode */}
      {isCompany ? (
        <View
          style={[
            styles.mascot,
            tvDisplayMode && styles.tvMascot,
            isSmallScreen && !tvDisplayMode && styles.mascotSmall,
          ]}
          pointerEvents="none"
        >
          <Image
            source={{ uri: mascotUri }}
            style={[
              styles.mascotImage,
              tvDisplayMode && styles.tvMascotImage,
              isSmallScreen && !tvDisplayMode && styles.mascotImageSmall,
            ]}
            contentFit="contain"
            cachePolicy="memory-disk"
            alt={WEB_IMAGE_ALT_DECORATIVE}
          />
        </View>
      ) : null}
    </View>
  );
};
